import { useCallback, useMemo, useRef, useState } from 'react';
import { App as AntdApp } from 'antd';

import { saveItemCells, isStaleItem } from '../../services/items';
import { needsAttestation } from '../../services/mfa';
import { getElementValidationErrors } from '../../utils/elements';
import { missingRequired } from '../../utils/required';
import { normalizeId } from '../../utils/ids';
import { byNameOf } from './cardTableModel';
import { GRANT_READ_ONLY_WORDS, isGrantReadOnly } from '../../utils/elementGrantRefusals';
import { conflictValueText, sameValue as same } from './staleConflict';

/**
 * The item table's inline edit: one cell open at a time, saved optimistically
 * through the form's write path with a rollback, and every refusal said in
 * the cell it is about.
 *
 *   editing   { itemId, name, value?, error? } — the open cell, or null. On
 *             a refusal the cell REOPENS holding what was typed and the
 *             reason, so nothing the reader typed is lost to an error.
 *   pending   itemId -> the row as it will stand, drawn while the save is in
 *             flight. A row with a save in flight cannot be edited again:
 *             its next save would carry the old `updatedAt` and be refused
 *             as stale by its own first half.
 *
 * What is checked BEFORE the request is the form's own check, from the same
 * modules: the value's own rules (utils/elements getElementValidationErrors
 * — bounds, formats) and required / requiredWhen over the row AS IT WOULD
 * STAND (utils/required missingRequired, the file the API vendors), because
 * the API asks required of the merged map and one cell can make another
 * field required.
 *
 * `onItemUpdated(item)` hands the settled document back to the page's feed.
 * A 409 `stale_item` hands back the item AS IT STANDS (the API sends it),
 * and the ruling on it (#6 of the card-table review) is about the ONE field
 * edited, not the row: most 409s on a table are a background recompute (a
 * formula, an automation) or a colleague's save of ANOTHER field moving
 * `updatedAt`, and refusing those would make a busy table uneditable.
 *   - the field still holds what this browser read → retry ONCE, with the
 *     returned `updatedAt`; nothing to ask, because nothing was overwritten;
 *   - the field itself moved → a conflict: `conflict` (below) opens
 *     ItemConflictModal — the field's row, Yours and theirs, headed with
 *     who changed it and when — the row showing theirs meanwhile; choosing
 *     Yours saves again against the new version, choosing theirs or
 *     Cancel sends nothing.
 * A second 409 on the retry is said, not retried again. The judgement is
 * staleConflict.js's, the one the item form makes of every field.
 *
 *   conflict  { item, since, conflicts, onSave, onCancel } — the open
 *             "Someone changed this field" question, or null. The table
 *             renders it (ItemsTable).
 * A 428 `attestation_required` opens the card's signature prompt (the form's
 * flow) and retries the same request signed.
 */
export default function useCellEdit({ elementDefs = [], onItemUpdated, onReload }) {
	const { message } = AntdApp.useApp();
	const [editing, setEditing] = useState(null);
	const [pending, setPending] = useState({});
	const [attestation, setAttestation] = useState(null);
	const [conflict, setConflict] = useState(null);
	const pendingRef = useRef(pending);
	pendingRef.current = pending;
	// Keep mine (a stale-conflict answer) re-runs commit against the version
	// the 409 handed back; the ref is the commit of the latest render.
	const commitRef = useRef(null);

	const open = useCallback((item, name) => {
		const id = normalizeId(item?._id);
		if (!id || pendingRef.current[id]) return;
		setEditing({ itemId: id, name });
	}, []);

	const cancel = useCallback(() => setEditing(null), []);

	const byName = useMemo(() => byNameOf(elementDefs), [elementDefs]);
	const labelOf = useCallback(name => {
		const def = byName.get(name);
		return def?.label?.singular || name;
	}, [byName]);
	const textOf = useCallback((name, value) => conflictValueText(byName.get(name), value), [byName]);

	const commit = useCallback(async (item, name, value) => {
		const id = normalizeId(item?._id);
		const before = item?.elements || {};
		if (same(before[name], value)) {
			setEditing(null);
			return;
		}
		const merged = { ...before, [name]: value };

		// The form's own check, before anything leaves the browser.
		const own = getElementValidationErrors(elementDefs, merged, { stored: before })[name];
		const required = missingRequired(elementDefs, merged);
		const refusal = own
			|| (required.find(entry => entry.element === name)?.message)
			|| (required.length ? `${required[0].message} Open the item to fill in ${labelOf(required[0].element)} too.` : null);
		if (refusal) {
			setEditing({ itemId: id, name, value, error: refusal });
			return;
		}

		setEditing(null);
		setPending(prev => ({ ...prev, [id]: { ...item, elements: merged } }));
		const settle = () => setPending(prev => {
			const next = { ...prev };
			delete next[id];
			return next;
		});

		const send = (ifUpdatedAt, config) => saveItemCells(id, { [name]: value }, {
			ifUpdatedAt: ifUpdatedAt || null,
			config,
		});
		const withAttestation = ifUpdatedAt => send(ifUpdatedAt).catch(error => {
			if (!needsAttestation(error)) throw error;
			return new Promise((resolve, reject) => {
				setAttestation({
					mode: error?.response?.data?.mode || 'pin',
					cardId: normalizeId(item?.card_id),
					onSigned: token => send(ifUpdatedAt, { headers: { 'X-Attestation': token } }).then(resolve, reject),
					onCancel: () => reject(error),
				});
			});
		});

		let saved;
		try {
			try {
				saved = await withAttestation(item?.updatedAt);
			} catch (first) {
				const current = isStaleItem(first) ? first?.response?.data?.item : null;
				if (!current || !current._id) throw first;
				if (!same(current.elements?.[name], before[name])) {
					// The field itself moved: the reader decides.
					settle();
					onItemUpdated?.(current);
					if (same(current.elements?.[name], value)) return; // they typed what it now says
					setConflict({
						item: current,
						since: item?.updatedAt || null,
						conflicts: [{ name, label: labelOf(name), mine: textOf(name, value), theirs: textOf(name, current.elements?.[name]) }],
						onSave: (choices) => {
							setConflict(null);
							// Theirs is already in the row: only "Yours" sends.
							if (choices?.[name] === 'mine') commitRef.current?.(current, name, value);
						},
						onCancel: () => setConflict(null),
					});
					return;
				}
				// Somebody (or keepr) moved ANOTHER value: this one still reads
				// as it did, so saving it overwrites nothing. Once.
				setPending(prev => ({ ...prev, [id]: { ...current, elements: { ...(current.elements || {}), [name]: value } } }));
				saved = await withAttestation(current.updatedAt);
			}
			settle();
			if (saved && saved._id) onItemUpdated?.(saved);
		} catch (error) {
			// Rollback first: the row goes back to what the server still holds.
			settle();
			if (isStaleItem(error)) {
				const current = error?.response?.data?.item;
				if (current && current._id) onItemUpdated?.(current);
				else onReload?.();
				message.warning(`Somebody changed this item since the list loaded, so your change to ${labelOf(name)} was not saved. The row now shows what it says.`);
				return;
			}
			if (needsAttestation(error)) {
				message.info(`Not saved — this card asks you to sign each save. Your change to ${labelOf(name)} was dropped.`);
				return;
			}
			const data = error?.response?.data;
			const text = (isGrantReadOnly(data) && GRANT_READ_ONLY_WORDS)
				|| (typeof data?.message === 'string' && data.message)
				|| (typeof data === 'string' && data)
				|| 'Something went wrong, and the change was not saved.';
			// A refusal about ANOTHER field (a requiredWhen the server saw that
			// this browser did not) still lands here, in the cell being edited:
			// it is the edit that caused it.
			setEditing({ itemId: id, name, value, error: text });
		}
	}, [elementDefs, labelOf, textOf, message, onItemUpdated, onReload]);
	commitRef.current = commit;

	return { editing, pending, open, cancel, commit, attestation, setAttestation, conflict };
}
